import React, { useState, useMemo } from 'react';
import { GenericTable } from '@shared/components/GenericTable';
import { TableColumn } from '@shared/types/table';
import { SearchInput } from '@shared/components/SearchInput';
import { getStatusBadgeClass } from '@shared/utils/statusUtils';
import { Eye } from 'lucide-react';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { useNavigate } from 'react-router-dom';
import { useOperations } from '../hooks';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { PAGE_SIZE } from '@shared/utils/constants';

export const PendingList = () => {
    const utilityId = useRemoteUtilityId();
    const [currentPage, setCurrentPage] = useState(1);
    const [searchData, setSearchData] = useState('');
    const { data: operationData, isLoading } = useOperations(utilityId, 'pending', currentPage, PAGE_SIZE, searchData);
    const navigate = useNavigate();

    const data = operationData?.results?.map((item: any) => ({
        id: item.id,
        operationId: item.operationNo || 'N/A',
        name: item.name || 'N/A',
        type: item.operationTypeDisplay || 'N/A',
        assignedTo: item.assignedTo || 'N/A',
        route: item.route || 'N/A',
        scheduledDate: item.scheduledDate || '--',
        completedDate: item.completedDate || '--',
        createdDate: item.createdDate || '—',
        createdBy: item.createdBy || 'NA',
        status: item.statusDisplay || 'N/A'
    })) || [];

    const totalItems = operationData?.count || 0;

    const pagination = useMemo(
        () => ({
            currentPage,
            totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
            totalItems: totalItems,
            itemsPerPage: PAGE_SIZE,
            startItem: totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
            endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
        }),
        [currentPage, totalItems]
    );

    const columns: TableColumn<any>[] = [
        {
            key: 'operationId',
            header: 'Operation ID',
        },
        {
            key: 'type',
            header: 'Type',
            render: (value) => {
                let variantClass = '';
                if (value === 'Replacement') variantClass = 'bg-purple-100 text-purple-800 border-purple-200';
                else if (value === 'Repair') variantClass = 'bg-orange-100 text-orange-800 border-orange-200';
                else if (value === 'Disconnect') variantClass = 'bg-red-100 text-red-800 border-red-200';
                else variantClass = 'bg-gray-100 text-gray-800 border-gray-200';

                return <Badge variant="outline" className={`font-normal ${variantClass}`}>{value}</Badge>;
            }
        },
        { key: 'scheduledDate', header: 'Scheduled Date' },
        {
            key: "createdDate",
            header: "Created",
            type: "custom",
            render: (value, row) => (
                <div className="flex flex-col">
                    <span className="font-semibold text-gray-900">{value ? String(value).replace(/-/g, "/") : "—"}</span>
                    <p className="text-[10px] text-gray-500 mt-0.5">{row.createdBy || "NA"}</p>
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (value) => <Badge variant="outline" className={`font-normal ${getStatusBadgeClass(value)}`}>{value}</Badge>
        },
        {
            key: 'actions',
            header: 'Action',
            render: (val, row) => (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-gray-100"
                    onClick={() => {
                        const typeParam = row.type ? `&type=${row.type.toLowerCase()}` : '';
                        navigate(`/mx/operations/${row.id}?from=pending${typeParam}`);
                    }}
                >
                    <Eye className="h-4 w-4 text-gray-400" />
                </Button>
            )
        }
    ];

    return (
        <div className="space-y-6 pt-4">
            <GenericTable
                data={data}
                loading={isLoading}
                columns={columns}
                rowKey="operationId"
                pagination={pagination}
                onPageChange={(page) => setCurrentPage(page)}
                emptyMessage="No operations found."
                searchPlaceholder="Search by ID, name, type, personnel..."
                onSearch={(val) => {
                    setSearchData(val);
                    setCurrentPage(1);
                }}
            />
        </div>
    );
};
